// Copyright (c) Docugami, Inc. All rights reserved.

import { GraphPicker, GraphPickerSource } from "components/storage/GraphPicker";
import { useStorageProviderToken } from "hooks/StorageProviderToken";
import { OAuthRequestStatus, Token } from "networking/Authentication";
import { ProviderId } from "networking/StorageProviders";
import { useEffect, useState } from "react";
import { StorageProviderItemsSelectedSchema, StorageProviderOpenedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { BoxPicker } from "./BoxPicker";
import { DropboxPicker } from "./DropboxPicker";
import { ProviderPickerItem } from "./ProviderPickerItem";
import { SyncModal } from "./SyncModal";

export enum ItemTypeFilter {
    All = 'All',
    File = 'File',
    Folder = 'Folder'
}

export enum SelectionMode {
    Multiple = 'Multiple',
    Single = 'Single'
}

export interface FilePickerProps {
    token: Token;
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: (items: ProviderPickerItem[]) => void;
    itemTypeFilter: ItemTypeFilter,
    selectionMode: SelectionMode
}

interface StorageProviderFilePickerProps {
    providerId: ProviderId | null;
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: (items: ProviderPickerItem[]) => void;
    itemTypeFilter?: ItemTypeFilter,
    selectionMode?: SelectionMode
}

export const StorageProviderFilePicker = (props: StorageProviderFilePickerProps): JSX.Element => {
    const [pickedFolders, setPickedFolders] = useState<ProviderPickerItem[]>([]);

    const { token, getToken, statusModal, status: tokenStatus } = useStorageProviderToken(props.providerId, props.onCancel);

    useEffect(() => {
        if (props.isOpen && !!props.providerId) {
            Telemetry.trackEvent<StorageProviderOpenedSchema>({
                eventName: TelemetryEvent.StorageProviderOpened,
                providerId: props.providerId
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen])

    useEffect(() => {
        if (props.isOpen && !token) {
            if (!props.providerId) {
                throw new Error("Must specify a provider ID before opening a picker");
            }

            getToken();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.isOpen, token]);

    useEffect(() => {
        if (tokenStatus === OAuthRequestStatus.Failed || tokenStatus === OAuthRequestStatus.Timeout) {
            props.onCancel();
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [tokenStatus]);

    function confirm(items: ProviderPickerItem[]) {
        props.onConfirm(items);
        if (!!props.providerId) {
            Telemetry.trackEvent<StorageProviderItemsSelectedSchema>({
                eventName: TelemetryEvent.StorageProviderItemsSelected,
                providerId: props.providerId,
                numOfItems: items.length
            });
        }
    }

    function handlePickerSelection(items: ProviderPickerItem[]) {
        const pickedFolderItems = items.filter(item => item.isFolder);
        if (!!pickedFolderItems.length) {
            // There is a folder within the selected items
            // Show the folder sync setting experience
            setPickedFolders(pickedFolderItems);
        }
        else {
            confirm(items);
        }
    }

    function getPicker() {
        if (!token || !props.providerId) {
            return null;
        }

        const pickerProps: FilePickerProps = {
            token: token,
            isOpen: props.isOpen,
            onCancel: props.onCancel,
            onConfirm: handlePickerSelection,
            itemTypeFilter: props.itemTypeFilter ?? ItemTypeFilter.All,
            selectionMode: props.selectionMode ?? SelectionMode.Multiple
        };

        return pickerFactory(props.providerId, pickerProps);
    }

    function getSyncPicker() {
        if (!token || !props.providerId || !pickedFolders.length) {
            return null;
        }

        return <SyncModal providerId={props.providerId} items={pickedFolders} onConfirm={confirm} />
    }

    return (
        <>
            {statusModal}
            {getPicker()}
            {getSyncPicker()}
        </>
    );
}

function pickerFactory(id: ProviderId, props: FilePickerProps) {
    switch (id) {
        case ProviderId.OneDrive: {
            return <GraphPicker {...props} defaultSource={GraphPickerSource.OneDrive} sourceFilter={[GraphPickerSource.OneDrive]} />
        }

        case ProviderId.SharePoint: {
            return <GraphPicker {...props} defaultSource={GraphPickerSource.SharePoint} sourceFilter={[GraphPickerSource.SharePoint]} />
        }

        case ProviderId.Box: {
            return <BoxPicker {...props} />
        }

        case ProviderId.Dropbox: {
            return <DropboxPicker {...props} />
        }

        default:
            throw new Error('No supported picker for storage provider');
    }
}
